export type TimelineEventGroup = 'policy' | 'verification' | 'billing';

export interface TimelineEventType {
  /** What the backend sends. */
  value: string;
  /** What a url carries — the label, not the wire value. */
  slug: string;
  label: string;
  group: TimelineEventGroup;
  isDefaultOn: boolean;
}

export const TIMELINE_EVENT_GROUPS: { value: TimelineEventGroup; label: string }[] = [
  { value: 'policy', label: 'Policy' },
  { value: 'verification', label: 'Verification' },
  { value: 'billing', label: 'Billing' }
];

export const TIMELINE_EVENT_TYPES: TimelineEventType[] = [
  {
    value: 'policy_created',
    slug: 'created',
    label: 'Created',
    group: 'policy',
    isDefaultOn: true
  },
  {
    value: 'plan_start',
    slug: 'plan_start',
    label: 'Plan Start Date',
    group: 'policy',
    isDefaultOn: true
  },
  {
    value: 'plan_end',
    slug: 'plan_end',
    label: 'Plan End Date',
    group: 'policy',
    isDefaultOn: true
  },
  {
    value: 'policy_field_change',
    slug: 'policy_updated',
    label: 'Policy Updated',
    group: 'policy',
    isDefaultOn: true
  },
  {
    value: 'insurance_verification',
    slug: 'vob',
    label: 'VOB',
    group: 'verification',
    isDefaultOn: true
  },
  {
    value: 'external_policy_verification',
    slug: 'external_vob',
    label: 'External VOB',
    group: 'verification',
    isDefaultOn: true
  },
  {
    value: 'billing_item',
    slug: 'billing_items',
    label: 'Billing Items',
    group: 'billing',
    isDefaultOn: true
  },
  {
    value: 'case_rate_period',
    slug: 'case_rate',
    label: 'Case Rate',
    group: 'billing',
    isDefaultOn: true
  },
  {
    value: 'claim_submitted',
    slug: 'claim_submitted',
    label: 'Claim Submitted',
    group: 'billing',
    isDefaultOn: true
  },
  {
    value: 'claim_status_change',
    slug: 'claim_status_change',
    label: 'Claim Status Change',
    group: 'billing',
    isDefaultOn: true
  },
  {
    value: 'remit_received',
    slug: 'remit_received',
    label: 'Remit Received',
    group: 'billing',
    isDefaultOn: true
  }
];

export const TIMELINE_PRESETS: { value: string; label: string; groups: TimelineEventGroup[] }[] = [
  { value: 'everything', label: 'Everything', groups: ['policy', 'verification', 'billing'] },
  {
    value: 'policy_verification',
    label: 'Policy + Verification',
    groups: ['policy', 'verification']
  },
  { value: 'policy_billing', label: 'Policy + Billing', groups: ['policy', 'billing'] }
];

export const DEFAULT_EVENT_TYPES = TIMELINE_EVENT_TYPES.filter((type) => type.isDefaultOn).map(
  (type) => type.value
);

export function eventTypesInGroup(group: TimelineEventGroup): string[] {
  return TIMELINE_EVENT_TYPES.filter((type) => type.group === group).map((type) => type.value);
}

export function presetEventTypes(presetValue: string): string[] {
  const preset = TIMELINE_PRESETS.find((candidate) => candidate.value === presetValue);

  return preset ? preset.groups.flatMap(eventTypesInGroup) : [];
}

export function matchingPreset(selectedEventTypes: Set<string>): string | null {
  const preset = TIMELINE_PRESETS.find((candidate) => {
    const types = presetEventTypes(candidate.value);
    return (
      types.length === selectedEventTypes.size &&
      types.every((type) => selectedEventTypes.has(type))
    );
  });

  return preset ? preset.value : null;
}

// A url may carry either the slug or the wire value; both resolve.
export function eventTypeFromToken(token: string): string | null {
  const match = TIMELINE_EVENT_TYPES.find((type) => type.slug === token || type.value === token);

  return match ? match.value : null;
}

export function eventTypeSlug(value: string): string {
  const match = TIMELINE_EVENT_TYPES.find((type) => type.value === value);

  return match ? match.slug : value;
}
